import React, { useState } from "react";
import { Moon, Sun, HelpCircle } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Tooltip from "../common/Tooltip";
import PartnerLogos from "./PartnerLogos";

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);

  return (
    <>
      <header className="bg-white dark:bg-gray-900 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 py-4">
            {/* Left side - Help Button */}
            <div className="flex-1 flex items-center justify-start">
              <Tooltip
                content="Get help with booking your consultation"
                position="bottom"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHelp(true)}
                  className="p-2"
                  aria-label="Help"
                >
                  <HelpCircle className="w-5 h-5" />
                </Button>
              </Tooltip>
            </div>
            {/* Centered Logo */}
            <div className="flex justify-center px-8">
              <div className="py-3 flex flex-col items-center">
                <img
                  src="/logo.png"
                  alt="Alpha Air Logo"
                  className="h-10 sm:h-12 md:h-16 w-auto transition-all duration-200"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
            {/* Navigation and Actions */}
            <div className="flex-1 flex items-center justify-end space-x-2 sm:space-x-4">
              {/* Theme Toggle */}
              <Tooltip
                content={`Switch to ${
                  theme === "light" ? "dark" : "light"
                } mode`}
                position="bottom"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className="p-2"
                  aria-label={`Switch to ${
                    theme === "light" ? "dark" : "light"
                  } mode`}
                >
                  {theme === "light" ? (
                    <Moon className="w-5 h-5" />
                  ) : (
                    <Sun className="w-5 h-5" />
                  )}
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </header>

      {/* Partner Logos */}
      <PartnerLogos />

      {/* Help Modal */}
      <Modal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Need Help?"
        size="md"
      >
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Booking Your Consultation or Service Visit
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
              <li>Enter your contact details</li>
              <li>
                Select your request type:{" "}
                <strong>Quote / Service / Warranty</strong>. Then choose your
                heating or cooling system, describe your needs, and—if
                possible—upload plans or images.
              </li>
              <li>Pick an available time slot</li>
              <li>Review and confirm your booking</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Need More Help?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Call us at{" "}
              <a
                href="tel:1300854525"
                className="text-primary hover:text-primary-700 font-medium"
              >
                1300 854 525
              </a>{" "}
              or email{" "}
              <a
                href="mailto:info@alphaair.com.au"
                className="text-primary hover:text-primary-700 font-medium break-all"
              >
                info@alpha-air.com.au
              </a>
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Header;
